import { WSInfoBox, WSList } from "@wingspanhq/fe-component-library";
import React from "react";
import { useHistory } from "react-router-dom";

import { useFocusedFeatures } from "../../../../../FocusedFeatures";
import { useUserId } from "../../../../../query/hooks/helpers";
import { useQueryCustomerEntity } from "../../../../../query/onboarding/queries/useQueryCustomerEntity";
import { useQueryVerifications } from "../../../../../query/onboarding/queries/useQueryVerifications";
import {
  useQueryMemberProfile,
  useQueryUserProfile
} from "../../../../../query/users/queries";
import { DataView } from "../../../../../shared/components/DataView";
import { normalizeDateOnly } from "../../../../../shared/utils/normalizeDateOnly";
import { useIsTaxDocumentsExist } from "../../../../TaxDocuments/utils/useIsTaxDocumentsExist";
import { PATH_ONBOARDING_US_PERSON_INFO } from "../../../../Onboarding";
import { usePayoutMethodMissingData } from "../../../components/AlertPayoutMethodMissingData";
import { DataViewTaxpayerInformation } from "../../../components/DataViewTaxpayerInformation";
import { alertMissingDataProps } from "../../../constants/alerts";
import { useModalEditAddress } from "../components/FormEditAddress";
import { useModalEditPreferredName } from "../components/FormEditPreferredName";
import { useModalEditWebsite } from "../components/FormEditWebsite";
import { selectorIsUsPersonVerificationAvailable } from "../../../selectors/selectorIsUsPersonVerificationAvailable";
import { useFeatureFlags } from "../../../../../query/hooks/useFeatureFlags";
import { ReviewUSPerson } from "../../../../Onboarding/components/ReviewUSPerson";
import { selectorUsPersonPartialFormData } from "../../../../Onboarding/selectors/selectorUsPersonPartialFormData";
import { useQueryUserProfileV2 } from "../../../../../query/user/queries/useQueryUserProfile";
import { selectorIsUsInternationalPersonVerified } from "../../../selectors/selectorIsUsInternationalPersonVerified";

type Props = {};

export const RoutePersonalInformation: React.FC<Props> = () => {
  const userId = useUserId();
  const history = useHistory();
  const queryUser = useQueryUserProfile(userId);
  const queryUserProfile = useQueryUserProfileV2();
  const queryMember = useQueryMemberProfile(userId);
  const featureFlags = useFeatureFlags();
  const queryMonitoredEntity = useQueryCustomerEntity({
    refetchOnMount: true
  });
  const queryVerifications = useQueryVerifications({
    refetchOnMount: true
  });

  const modalEditPreferredName = useModalEditPreferredName();
  const modalEditWebsite = useModalEditWebsite();
  const modalEditAddress = useModalEditAddress();
  const focusedFeatures = useFocusedFeatures();
  const showOldTaxDocuments = useIsTaxDocumentsExist(2023);

  const payoutMethodMissingData = usePayoutMethodMissingData();

  if (!queryUser.data || !queryMember.data) {
    return null;
  }

  const user = queryUser.data;
  const member = queryMember.data;

  return (
    <WSList gap="2XL">
      <WSInfoBox title="Please ensure your information is up to date and accurate">
        <ol>
          <li>Click edit to edit your personal information.</li>
          <li>
            Your taxpayer information cannot be edited once your account has
            been verified (green checkmark).
          </li>
          <li>To make changes to verified fields, click “Request update”.</li>
          {showOldTaxDocuments ||
          focusedFeatures.showSideNavTaxDocumentsSettings ? (
            <li>
              To view your tax form delivery preferences, go to{" "}
              <a
                about="_self"
                onClick={() => history.push("/member/tax-documents")}
              >
                Tax Documents
              </a>
            </li>
          ) : null}
          {featureFlags.data?.internationalUsPerson &&
          selectorIsUsPersonVerificationAvailable(
            queryMonitoredEntity.data,
            queryVerifications.data
          ) ? (
            <li>
              If you are a U.S. person (U.S. citizen, green card holder, or tax
              resident living abroad) for tax reporting purposes,{" "}
              <a
                onClick={() => {
                  history.push(PATH_ONBOARDING_US_PERSON_INFO);
                }}
              >
                click here
              </a>{" "}
              to enter your U.S. information
            </li>
          ) : null}
        </ol>
      </WSInfoBox>
      <DataViewTaxpayerInformation
        member={member}
        missingFields={payoutMethodMissingData?.missingProfileFields}
        monitoredEntity={queryMonitoredEntity.data}
        user={user}
        verificationStatus={queryVerifications.data?.tax}
      />
      {featureFlags.data?.internationalUsPerson &&
      selectorIsUsInternationalPersonVerified(
        queryMonitoredEntity.data,
        queryVerifications.data
      ) ? (
        <ReviewUSPerson
          title="U.S person information"
          verified
          formData={selectorUsPersonPartialFormData({
            dob: queryUser.data?.profile.dob,
            userProfile: queryUserProfile.data
          })}
        />
      ) : null}

      <DataView
        data={[
          {
            label: {
              text: "Preferred name",
              actionPosition: "left",
              action: {
                label: "Edit",
                onClick: () => {
                  modalEditPreferredName.open();
                }
              }
            },
            value: user.profile.preferredName || undefined
          },
          {
            label: "Birthday",
            value: normalizeDateOnly(user.profile.dob),
            fsExclude: true,
            formatterOptions: {
              dateType: "monthDayYear"
            }
          },
          {
            label: {
              text: "Business website",
              actionPosition: "left",
              action: {
                label: "Edit",
                onClick: () => {
                  modalEditWebsite.open();
                }
              }
            },
            value: member.profile.company?.website || "None"
          },
          {
            label: {
              text: "Personal address",
              actionPosition: "left",
              action: {
                label: "Edit",
                onClick: () => {
                  modalEditAddress.open();
                }
              }
            },
            value: member.profile.address
              ? [
                  member.profile.address.addressLine1,
                  member.profile.address.addressLine2,
                  [
                    member.profile.address.city,
                    member.profile.address.state,
                    member.profile.address.postalCode
                  ].join(" ")
                ]
                  .filter(Boolean)
                  .join("\n")
              : undefined,
            fsExclude: true,
            alert:
              payoutMethodMissingData?.missingAddressFields &&
              payoutMethodMissingData?.missingAddressFields.length > 0
                ? alertMissingDataProps
                : undefined
          },
          {
            label: "Contact info",
            value: `${user.email}\n${user.phone.number}`
          }
        ]}
        title="Personal information"
      />
    </WSList>
  );
};
